// The Scheduling page's rules and date helpers: ONE copy, used by the desktop Scheduling page
// (pages/SchedulingPage.jsx) and the phone (mobile/screens/SchedulingScreen.jsx).
// docs/mobile-pwa-plan.md "One copy of every action". Moved from SchedulingPage unchanged. Pure.

export const STATIONS = Object.freeze(['Laser', 'Tap', 'Brake', 'Fabrication', 'Blast', 'Finish']);

/** A Date as YYYY-MM-DD (its local day; the value of an <input type="date">). */
export function formatDate(dateObj) {
  if (!dateObj) return '';
  const yyyy = dateObj.getFullYear();
  const mm = String(dateObj.getMonth() + 1).padStart(2, '0');
  const dd = String(dateObj.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/** "Fri 3/7/25". */
export function formatDisplayDate(dateObj) {
  if (!dateObj) return '';
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const dayOfWeek = days[dateObj.getDay()];
  const month = dateObj.getMonth() + 1;
  const day = dateObj.getDate();
  const year = String(dateObj.getFullYear()).slice(-2);
  return `${dayOfWeek} ${month}/${day}/${year}`;
}

/** A YYYY-MM-DD (or the date part of a timestamp) as that LOCAL day. */
export function parseISODate(dateStr) {
  if (!dateStr) return null;
  if (dateStr.includes('T')) {
    dateStr = dateStr.split('T')[0];
  }
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day);
}

/** "H:MM:SS" (an interval as the API sends it) in hours; 0 for anything else. */
export function intervalToHours(intervalStr) {
  if (!intervalStr) return 0;
  const parts = intervalStr.split(':');
  if (parts.length !== 3) {
    return 0;
  }
  const [h, m, s] = parts.map(Number);
  if (isNaN(h) || isNaN(m) || isNaN(s)) {
    return 0;
  }
  return h + m / 60 + s / 3600;
}

/** A task's estimated hours. */
export function getHoursFromServer(task) {
  if (!task.estimated_time) return 0;
  return intervalToHours(task.estimated_time);
}

/** Row color: P1/P2 jobs pink, a task scheduled after its job's due date red. */
export function getTaskColorClass(task, job) {
  if (job && job.priority_level <= 2) {
    // Highlight the top two tiers (new P1 = Bad Idea negative on-hand, P2 = old P1).
    return 'bg-pink';
  }
  if (job && job.due_date) {
    const sched = parseISODate(task.scheduled_date);
    const due = parseISODate(job.due_date);
    if (sched && due && sched > due) {
      return 'bg-late';
    }
  }
  return '';
}

export function isWeekend(dateObj) {
  const day = dateObj.getDay(); // 0=Sun, 6=Sat
  return day === 0 || day === 6;
}

/** One alert per (job, station, type): the newest. */
export function getDedupedAlerts(allAlerts) {
  const dedupedMap = {};
  for (const al of allAlerts) {
    const key = `${al.job_id || 0}_${al.station || ''}_${al.alert_type}`;
    if (!dedupedMap[key]) {
      dedupedMap[key] = al;
    } else {
      const existing = dedupedMap[key];
      if (new Date(al.created_at) > new Date(existing.created_at)) {
        dedupedMap[key] = al;
      }
    }
  }
  return Object.values(dedupedMap);
}

// --- Days ------------------------------------------------------------------------------------------

/** Tasks by their scheduled day (YYYY-MM-DD), "Unscheduled" for none, in the order they came. */
export function tasksByDate(tasks) {
  const out = {};
  (tasks || []).forEach((task) => {
    const dk = task.scheduled_date ? task.scheduled_date.slice(0, 10) : 'Unscheduled';
    if (!out[dk]) out[dk] = [];
    out[dk].push(task);
  });
  return out;
}

/** A day's task ids after moving one up (delta -1) or down (+1): the phone's buttons for the
 *  desktop's drag. null when it can't move (already first / last, not on the day). */
export function moveTaskInDay(taskIds, taskId, delta) {
  const i = taskIds.indexOf(taskId);
  const j = i + delta;
  if (i < 0 || j < 0 || j >= taskIds.length) return null;
  const out = taskIds.slice();
  [out[i], out[j]] = [out[j], out[i]];
  return out;
}

// --- Second shift --------------------------------------------------------------------------------

/** GET /shifts/config as {defaults: {station: {enabled, hours}}, overrides: {"station|date": {...}},
 *  firstShiftHours (null when the API sent none)}. */
export function shiftConfigFromApi(data) {
  const defaults = {};
  (data?.defaults || []).forEach((d) => {
    defaults[d.workstation] = {
      enabled: !!d.second_shift_enabled,
      hours: Number(d.second_shift_hours) || 0,
    };
  });
  const overrides = {};
  (data?.overrides || []).forEach((o) => {
    overrides[`${o.workstation}|${o.shift_date}`] = {
      enabled: !!o.second_shift_enabled,
      hours: Number(o.second_shift_hours) || 0,
      note: o.note || '',
    };
  });
  return { defaults, overrides, firstShiftHours: data?.first_shift_hours ? Number(data.first_shift_hours) : null };
}

/** A (station, day)'s second shift: the day's override > the weekday default > off (weekends off). */
export function effectiveShift(defaults, overrides, station, dateObj) {
  const dateStr = formatDate(dateObj);
  const ovr = overrides[`${station}|${dateStr}`];
  if (ovr) return { ...ovr, source: 'override' };
  if (isWeekend(dateObj)) return { enabled: false, hours: 0, source: 'off' };
  const def = defaults[station];
  if (def && def.enabled) return { ...def, source: 'default' };
  return { enabled: false, hours: 0, source: 'off' };
}

/** Hours when the switch flips: turning it on with no hours starts at 4. */
export function hoursForToggle(effective, newEnabled) {
  return newEnabled && (!effective?.hours || effective.hours <= 0) ? 4 : effective?.hours || 0;
}

/** Typed hours, kept within 0-12 (anything unreadable = 0). */
export function clampShiftHours(input) {
  const parsed = parseFloat(input);
  return isNaN(parsed) ? 0 : Math.max(0, Math.min(12, parsed));
}

/** The hours to save when the hours box is left: the typed value kept within 0-12, or null when it
 *  is the day's hours already (nothing to save). */
export function hoursToSave(input, effective) {
  const hrs = clampShiftHours(input);
  return Math.abs(hrs - (effective?.hours ?? 0)) < 0.0001 ? null : hrs;
}

/** Tasks a job's schedule lists: every station but Shipping (Shipping has no schedule to set). */
export function jobScheduleTasks(tasks) {
  return (tasks || []).filter((t) => t.workstation !== "Shipping");
}

// --- Part-Based search ----------------------------------------------------------------------------

/** The active tasks for a part: exact matches on product id, part id or a batch task's part list
 *  when there are any, else partial matches on product id, description or the part list; sorted by
 *  scheduled day (none last) then job. */
export function partTaskMatches(allTasks, partInput) {
  const tasks = allTasks || [];
  const lowerInput = String(partInput || '').toLowerCase().trim();
  const exactMatches = tasks.filter((task) => {
    if (task.product_id && task.product_id.toLowerCase() === lowerInput) return true;
    if (task.part_id && String(task.part_id) === partInput) return true;
    if (task.contained_parts_str) {
      const containedParts = task.contained_parts_str.split(',').map((p) => p.trim().toLowerCase());
      if (containedParts.includes(lowerInput)) return true;
    }
    return false;
  });
  const filtered = exactMatches.length > 0 ? exactMatches : tasks.filter((task) => {
    if (task.product_id && task.product_id.toLowerCase().includes(lowerInput)) return true;
    if (task.product_description && task.product_description.toLowerCase().includes(lowerInput)) return true;
    if (task.contained_parts_str && task.contained_parts_str.toLowerCase().includes(lowerInput)) return true;
    return false;
  });
  filtered.sort((a, b) => {
    const dateA = a.scheduled_date || '9999-99-99';
    const dateB = b.scheduled_date || '9999-99-99';
    if (dateA !== dateB) return dateA.localeCompare(dateB);
    return a.job_id - b.job_id;
  });
  return filtered;
}
